import {
  getRobotIconName,
  loadAllRobotImages,
  registerRobotStyleImageMissingHandler,
} from '../RobotOnMapEditable/robotMapUtils'
import { Feature, Polygon } from 'geojson'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FillLayer, Layer, Source } from 'react-map-gl'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import { selectHighlightedFeature } from 'store/selectors/mission'
import { selectRobot } from 'store/selectors/robot'
import turf from 'turf'
import { RobotTelemetryData } from 'types/telemetry'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import { ROBOT_NETWORK_STATUSES, TRAJECTORY_LINE_WIDTH_IN_METERS } from 'utils/constants'
import { lineStringForTrajectoryPolygon } from 'utils/robotTrajectory'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { IRobotOnMap, IRobotTelemetry } from 'utils/interfaces'
import { centerMapTo, zoomMapTo } from 'utils/mapUtils'
import {
  hasRobotMapChassisGeometry,
  iconLayoutLatitude,
  robotIconOffsetPx,
  robotIconSizeAtFineZoom,
  robotIconSizeExpression,
} from 'utils/robotIconLayout'
import { VENDOR } from 'utils/vendorDisplayName'
import { telemetryPatchFromWs } from 'utils/wsTelemetryPatch'

import CenterOnRobotButton from './CenterOnRobotButton/CenterOnRobotButton'

const trajectoryStyle: any = {
  id: 'trajectory-style',
  type: 'fill',
  paint: {
    'fill-color': '#9747FF',
    'fill-opacity': 0.7,
  },
}

const RobotOnMap = ({
  map,
  offset = 600,
  highlightedMissionId,
  right,
  disableCenterButton,
  setPoseMode = false,
}: {
  map: mapboxgl.Map | undefined
  offset?: number
  highlightedMissionId?: number
  right?: number
  disableCenterButton?: boolean
  setPoseMode?: boolean
}) => {
  const robot = useSelector(selectRobot)
  const dispatch = useDispatch()

  const [robotOnMap, setRobotOnMap] = useState<IRobotOnMap>()
  const [robotZoomed, setRobotZoomed] = useState(false)
  const [robotCenteringMode, setRobotCenteringMode] = useState(true)
  const [centeringNonce, setCenteringNonce] = useState(0)
  const [trajectoryPolygon, setTrajectoryPolygon] = useState<Feature<Polygon> | undefined>()
  const highlightedFeature = useSelector(selectHighlightedFeature)

  // Track last WebSocket update time (client time when message was received) to prevent REST API from overwriting fresh WebSocket data
  // Using client time (when message was received) for accurate delta calculation regardless of robot/client clock sync
  const lastWebSocketUpdateTime = useRef<number>(0) // Client time (milliseconds) when last WebSocket message was received
  const previousWebSocketUpdateTime = useRef<number>(0) // Client time (milliseconds) when previous WebSocket message was received (for calculating period)
  const lastWebSocketTimestamp = useRef<number>(0) // Robot timestamp (header.stamp.sec + nsec/1e9) from last WebSocket message for detecting out-of-order messages (in seconds with nanosecond precision)
  const wsTelemetryPeriod = useRef<number>(0) // Calculated period between WebSocket messages (milliseconds), dynamically updated
  const WS_UPDATE_THRESHOLD_MS_DEFAULT = 5000 // Default threshold if period cannot be calculated (fallback)

  // Reset zoom state on component mount to ensure auto-zoom works on page refresh
  useEffect(() => {
    setRobotZoomed(false)
    // Reset WebSocket tracking refs on mount to allow REST API updates on page refresh
    lastWebSocketUpdateTime.current = 0
    previousWebSocketUpdateTime.current = 0
    lastWebSocketTimestamp.current = 0
    wsTelemetryPeriod.current = 0
  }, [])

  // When the robot is cleared from the Redux store remove the icon from the map.
  useEffect(() => {
    if (!robot.robotInfo) {
      setRobotOnMap(undefined)
    }
  }, [robot.robotInfo])

  // Initialize robot position and orientation from robotInfo.lastPosition and lastOrientation
  // This ensures the robot appears correctly on page load before WebSocket telemetry arrives
  // Also updates when lastOrientation changes (e.g., from periodic API updates in RobotView)
  // IMPORTANT: Only update from REST API if WebSocket hasn't updated recently to prevent conflicts
  // WebSocket telemetry will override this when it arrives, as it updates robotOnMap directly
  useEffect(() => {
    if (robot.robotInfo?.lastPosition && robot.robotInfo.lastPosition.coordinates) {
      // Calculate time since last WebSocket update (in milliseconds)
      // lastWebSocketUpdateTime.current stores the client time when last WebSocket message was received
      // This gives accurate delta regardless of robot/client clock synchronization
      const currentClientTime = Date.now()

      // Calculate delta: how many milliseconds have passed since last WebSocket update
      // If lastWebSocketUpdateTime is 0, it means WebSocket never sent data (or was reset)
      const timeSinceLastWsUpdate =
        lastWebSocketUpdateTime.current > 0 ? currentClientTime - lastWebSocketUpdateTime.current : Infinity // If no WebSocket data ever received, allow REST API update

      // Use dynamically calculated WebSocket telemetry period, or fallback to default threshold
      // If period is not yet calculated (0), use default threshold
      const wsUpdateThreshold =
        wsTelemetryPeriod.current > 0 ? wsTelemetryPeriod.current : WS_UPDATE_THRESHOLD_MS_DEFAULT

      // WebSocket is considered active if it updated within the calculated period (or default threshold)
      // This means: if WebSocket sent data less than one period ago (in client time), it's active
      // We ignore REST API updates if WebSocket is actively sending data (within one period)
      const isWebSocketActive = lastWebSocketUpdateTime.current > 0 && timeSinceLastWsUpdate < wsUpdateThreshold

      // Validate coordinates from REST API
      const restApiPosition = robot.robotInfo.lastPosition.coordinates
      const isValidRestApiLongitude =
        isFinite(restApiPosition[0]) && restApiPosition[0] >= -180 && restApiPosition[0] <= 180
      const isValidRestApiLatitude =
        isFinite(restApiPosition[1]) && restApiPosition[1] >= -90 && restApiPosition[1] <= 90

      if (!isValidRestApiLongitude || !isValidRestApiLatitude) {
        return
      }

      // Prefer live WS pose while it is fresh. Do not let a brief OFFLINE flap
      // pull REST lastPosition over a newer WebSocket marker (causes visible jumps).
      const shouldUpdateFromRestApi = !robotOnMap || !isWebSocketActive

      if (shouldUpdateFromRestApi) {
        if (robotOnMap && !isWebSocketActive) {
          const prev = robotOnMap.geometry.coordinates
          const next = robot.robotInfo.lastPosition.coordinates
          const jumpM =
            prev && next
              ? turf.distance(turf.point(prev as [number, number]), turf.point(next as [number, number]), 'meters')
              : null
          if (jumpM != null && jumpM > 0.5) {
            console.warn('[RobotOnMap] REST overwrite of map position', {
              serial: robot.robotInfo?.serialNumber,
              reason: 'ws_inactive',
              timeSinceLastWsUpdateMs: timeSinceLastWsUpdate === Infinity ? null : timeSinceLastWsUpdate,
              from: prev,
              to: next,
              jumpM,
            })
          }
        }
        const yaw =
          robot.robotInfo?.vendor === VENDOR.ASBIS
            ? 0
            : robot.robotInfo.lastOrientation
            ? getYawFromQuaternion(robot.robotInfo.lastOrientation)
            : 0

        const icon = getRobotIconName(robot.robotInfo.vendor, robot.robotInfo.robot_color, robot.robotInfo.robot_type)

        setRobotOnMap({
          id: 'robot_on_map',
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: robot.robotInfo.lastPosition.coordinates,
          },
          properties: {
            yaw,
            icon,
          },
        })
        // Note: icon-rotate is now data-driven (['get', 'yaw']), so no need to update robotStyle
      }
    }
  }, [robot.robotInfo?.lastPosition, robot.robotInfo?.lastOrientation, robot.robotInfo?.networkStatus])

  const lastCenteringCoords = useRef<[number, number] | null>(null)

  useEffect(() => {
    if (!robotOnMap || !map) return

    const coords = robotOnMap.geometry.coordinates as [number, number]

    if (!robotZoomed) {
      zoomMapTo(map as any, [robotOnMap], offset)
      setRobotZoomed(true)
      lastCenteringCoords.current = coords
      return
    }

    if (robotCenteringMode) {
      // Calculate distance moved since last centering to throttle camera updates
      let shouldCenter = !lastCenteringCoords.current
      if (lastCenteringCoords.current) {
        const distance = turf.distance(turf.point(lastCenteringCoords.current), turf.point(coords), 'meters')
        // Only center if robot moved more than 1 meter.
        // This eliminates flickering from tiny coordinate noise.
        if (distance > 1.0) {
          shouldCenter = true
        }
      }

      if (shouldCenter) {
        centerMapTo(map as any, [robotOnMap], offset)
        lastCenteringCoords.current = coords
      }
    }
  }, [robotZoomed, robotOnMap, map, robotCenteringMode, offset, centeringNonce])

  // Ensure robot and trajectory stay on top of other layers
  // Removed robotOnMap/trajectoryPolygon from dependencies to prevent flickering on every telemetry tick
  useEffect(() => {
    if (!map) return
    const layersToMove = ['trajectory-style', 'robot-image-style']
    layersToMove.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId)
      }
    })
  }, [map])

  useEffect(() => {
    if (highlightedFeature || highlightedMissionId) {
      setRobotCenteringMode(false)
    }
  }, [highlightedFeature, highlightedMissionId])

  useEffect(() => {
    if (!map) return
    const onStyleLoad = () => loadAllRobotImages(map)
    const onDragStart = () => setRobotCenteringMode(false)
    map.on('style.load', onStyleLoad)
    if (map.isStyleLoaded()) {
      onStyleLoad()
    }
    map.on('dragstart', onDragStart)
    return () => {
      map.off('style.load', onStyleLoad)
      map.off('dragstart', onDragStart)
    }
  }, [map])

  useEffect(() => {
    return registerRobotStyleImageMissingHandler(map)
  }, [map])

  useEffect(() => {
    const callback = (data: RobotTelemetryData) => {
      try {
        if (!robot.robotInfo?.serialNumber) {
          return
        }

        // If telemetry contains serial number, verify it matches our current robot
        if (data.robot_serial_number && data.robot_serial_number !== robot.robotInfo.serialNumber) {
          return
        }

        // Drop out-of-order / delayed frames before any Redux or map updates.
        // Robot header.stamp uses `nanosec` (Autonomics); accept `nsec` too.
        if (data.header?.stamp?.sec !== undefined) {
          const nsec = data.header.stamp.nsec || data.header.stamp.nanosec || 0
          const currentTimestamp = data.header.stamp.sec + nsec / 1e9
          if (lastWebSocketTimestamp.current > 0 && currentTimestamp < lastWebSocketTimestamp.current) {
            const position = data.odometry?.pose?.pose?.position
            console.warn('[RobotOnMap] out-of-order telemetry dropped', {
              serial: robot.robotInfo?.serialNumber,
              prevStamp: lastWebSocketTimestamp.current,
              stamp: currentTimestamp,
              stampDeltaSec: currentTimestamp - lastWebSocketTimestamp.current,
              to: position ? [position.x, position.y] : null,
              networkStatus: robot.robotInfo?.networkStatus,
            })
            return
          }
          lastWebSocketTimestamp.current = currentTimestamp
        }

        const telemetryPatch = telemetryPatchFromWs(data)
        if (telemetryPatch) {
          dispatch(RobotActions.setTelemetry(telemetryPatch))
        }

        // Update robot position only if odometry data is present
        // If odometry is missing, keep the last known position
        if (data.odometry?.pose?.pose?.position) {
          const position = data.odometry.pose.pose.position

          // Validate coordinates: longitude must be in [-180, 180], latitude in [-90, 90]
          // If coordinates are out of bounds, they might be in meters (UTM/local) instead of degrees (WGS84)
          const isValidLongitude = isFinite(position.x) && position.x >= -180 && position.x <= 180
          const isValidLatitude = isFinite(position.y) && position.y >= -90 && position.y <= 90

          if (!isValidLongitude || !isValidLatitude) {
            // Don't update position if coordinates are invalid
            return
          }

          const orientation = data.odometry.pose.pose.orientation || { x: 0, y: 0, z: 0, w: 1 }
          const yaw = getYawFromQuaternion(orientation)

          const currentClientTime = Date.now()

          // Update last WebSocket update time (client time when message was received) to prevent REST API from overwriting
          // Using client time (when message was received) for accurate delta calculation regardless of robot/client clock sync
          const previousWsUpdateTime = lastWebSocketUpdateTime.current

          // Calculate WebSocket telemetry period dynamically based on time between messages
          // This allows us to use the actual telemetry update frequency instead of a fixed threshold
          if (previousWsUpdateTime > 0) {
            // Calculate period as time between current and previous message
            const period = currentClientTime - previousWsUpdateTime
            // Update period only if it's reasonable (between 100ms and 30 seconds)
            // This filters out outliers (network delays, reconnections, etc.)
            if (period >= 100 && period <= 30000) {
              // Use exponential moving average to smooth out variations
              // First time: use measured period directly
              // Subsequent times: use weighted average (70% old, 30% new) for stability
              if (wsTelemetryPeriod.current === 0) {
                wsTelemetryPeriod.current = period
              } else {
                wsTelemetryPeriod.current = Math.round(wsTelemetryPeriod.current * 0.7 + period * 0.3)
              }
            }
          }

          // Update previous time before updating current time
          previousWebSocketUpdateTime.current = previousWsUpdateTime
          lastWebSocketUpdateTime.current = currentClientTime

          const icon = getRobotIconName(
            robot.robotInfo?.vendor,
            robot.robotInfo?.robot_color,
            robot.robotInfo?.robot_type
          )

          setRobotOnMap({
            id: 'robot_on_map',
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [position.x, position.y],
            },
            properties: {
              yaw,
              icon,
            },
          })
          // Note: icon-rotate is now data-driven (['get', 'yaw']), so no need to update robotStyle

          // Publish live position to Redux so LaserscanLayer (and others) can use real-time coords
          dispatch(RobotActions.setTelemetry({ livePosition: [position.x, position.y] }))
          dispatch(RobotActions.pushPositionToTrajectory([position.x, position.y]))
        }
      } catch (error) {
        console.error('[RobotOnMap] Error processing telemetry:', error)
      }
    }

    WebsocketManager.sendMessage(
      JSON.stringify({
        type: 'robot.subscribe',
        robot_serial_number: robot.robotInfo?.serialNumber,
      })
    )
    WebsocketManager.addEventListener(SOCKET_MESSAGES.ROBOT_TELEMETRY, callback)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.ROBOT_TELEMETRY, callback)
      if (robot.robotInfo?.serialNumber) {
        WebsocketManager.sendMessage(
          JSON.stringify({
            type: 'robot.unsubscribe',
            robot_serial_number: robot.robotInfo.serialNumber,
          })
        )
      }
    }
  }, [robot.robotInfo?.serialNumber, dispatch])

  useEffect(() => {
    const coordinates = robot.trajectory.geometry.coordinates
    if (coordinates.length < 2) {
      setTrajectoryPolygon(undefined)
      return
    }
    const handle = window.setTimeout(() => {
      try {
        const line = lineStringForTrajectoryPolygon(coordinates)
        if (!line) {
          setTrajectoryPolygon(undefined)
          return
        }
        setTrajectoryPolygon(turf.buffer(line, TRAJECTORY_LINE_WIDTH_IN_METERS, 'meters'))
      } catch (error) {
        console.error('[RobotOnMap] Error creating trajectory polygon:', error)
      }
    }, 200)
    return () => window.clearTimeout(handle)
  }, [robot.trajectory])

  const iconLatitude = iconLayoutLatitude(
    (robotOnMap?.geometry?.coordinates as number[] | undefined)?.[1] ?? robot.robotInfo?.lastPosition?.coordinates?.[1]
  )
  const robotStyle = useMemo<any>(() => {
    let opacity = 1.0
    if (setPoseMode) {
      opacity = 0.3
    } else if (robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE) {
      opacity = 0.6
    }
    const lengthM = robot.robotInfo?.lengthM
    const originToFrontM = robot.robotInfo?.originToFrontM
    const hasChassis = hasRobotMapChassisGeometry(lengthM, originToFrontM)
    return {
      id: 'robot-image-style',
      type: 'symbol',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-anchor': 'center',
        'icon-rotation-alignment': 'map',
        'icon-size': robotIconSizeExpression(robotIconSizeAtFineZoom(hasChassis, iconLatitude)),
        'icon-offset': ['literal', robotIconOffsetPx(originToFrontM)],
        'icon-rotate': ['get', 'yaw'],
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': opacity,
        'icon-opacity-transition': { delay: 0, duration: 0 },
      },
    }
  }, [
    setPoseMode,
    robot.robotInfo?.networkStatus,
    robot.robotInfo?.lengthM,
    robot.robotInfo?.originToFrontM,
    iconLatitude,
  ])

  return (
    <>
      {trajectoryPolygon?.geometry.coordinates.length && robotOnMap && (
        <Source id="cleaning-route" type="geojson" data={trajectoryPolygon}>
          <Layer
            {...trajectoryStyle}
            beforeId={map && map.getLayer('robot-image-style') ? 'robot-image-style' : undefined}
          />
        </Source>
      )}
      {robotOnMap && (
        <Source id="robot" type="geojson" data={robotOnMap}>
          <Layer
            key={`robot-layer-${setPoseMode ? 'pose' : 'normal'}-${robot.robotInfo?.networkStatus}`}
            {...robotStyle}
          />
        </Source>
      )}
      <CenterOnRobotButton
        right={right}
        disabled={disableCenterButton}
        onClick={() => {
          setRobotCenteringMode(true)
          setCenteringNonce(n => n + 1)
          lastCenteringCoords.current = null // Reset ref to force immediate centering
        }}
      />
    </>
  )
}

export default RobotOnMap
